import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { getLocalTimezone, zonedWallToUtc } from "@/lib/eventDatetime";
import type { ReminderOffset } from "../types";
import { DEFAULT_EVENT_ICON } from "../lib/eventIcons";
import { breakfastEventsPrefix, pendingInvitesPrefix } from "../queryKeys";
import { circlesSchedulePrefix } from "@/features/circles/queryKeys";
import { trackedDaysPrefix } from "@/hooks/queries/useTrackedDays";
import {
  describeEventInviteError,
  describeEventInvitePayloadError,
} from "../lib/describeEventInviteError";

/**
 * Create a new breakfast (social) event plus its attendee / potluck fan-out.
 *
 * ## Timezone (Phase 24 migration)
 *
 * `breakfast_events.starts_at` is `TIMESTAMPTZ NOT NULL`. The form still
 * authors in plain wall-clock (`date` + `time`) from the host's perspective,
 * but the DB demands an absolute instant. We build both instants here using
 * the viewer's current IANA zone — that's the "author zone" the event is
 * saved under, matching Google/Outlook's "create in your current timezone"
 * default.
 *
 * Note: we deliberately moved away from `rpc("create_breakfast_event")`
 * because that function's signature doesn't include the new columns
 * (`starts_at` / `ends_at` / `event_timezone`), so every RPC call
 * post-migration hits the `starts_at NOT NULL` constraint. The direct
 * INSERT below writes every required field.
 */
export function useCreateEvent() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (params: {
      title: string;
      date: string;
      time: string;
      ends: string;
      type: "potluck" | "outside";
      eventTag: string;
      /** Host-selected glyph key — see `lib/eventIcons.ts`. */
      icon?: string;
      location?: string;
      inviteeIds: string[];
      potluckItems?: Array<{ item_name: string }>;
      /**
       * Optional reminder offset — "N minutes before event_time".
       * NULL / undefined = no reminder. Allowed values enforced by
       * the `breakfast_events_reminder_offset_chk` CHECK constraint.
       */
      reminderOffsetMinutes?: ReminderOffset | null;
    }) => {
      const { data: { session } } = await supabase.auth.getSession();
      const hostId = session?.user?.id;
      if (!hostId) throw new Error("Not authenticated");

      const hostTz = getLocalTimezone();
      const startsAt = zonedWallToUtc(params.date, params.time, hostTz);
      const endsAt = params.ends
        ? zonedWallToUtc(params.date, params.ends, hostTz)
        : null;

      // 1. Insert the event row. RLS enforces `host_id = auth.uid()`.
      //    `.select().single()` gives us the server-assigned id + row back
      //    so we can fan out attendees / potluck_items in the next steps.
      const { data: inserted, error: insertErr } = await supabase
        .from("breakfast_events")
        .insert({
          host_id:          hostId,
          title:            params.title,
          event_date:       params.date,
          event_time:       params.time + ":00",
          event_ends:       params.ends + ":00",
          event_type:       params.type,
          event_tag:        params.eventTag,
          icon:             params.icon || DEFAULT_EVENT_ICON,
          location_or_link: params.location || null,
          starts_at:        startsAt,
          ends_at:          endsAt,
          event_timezone:   hostTz,
          reminder_offset_minutes: params.reminderOffsetMinutes ?? null,
        })
        .select()
        .single();

      if (insertErr) throw new Error(insertErr.message);
      if (!inserted) throw new Error("Event could not be created.");

      const eventId = inserted.id as string;

      // 2. Host is always a confirmed attendee.
      const attendeeRows: Array<{
        event_id: string;
        user_id: string;
        status: "confirmed" | "pending";
      }> = [{ event_id: eventId, user_id: hostId, status: "confirmed" }];

      if (params.inviteeIds?.length) {
        for (const uid of params.inviteeIds) {
          if (uid !== hostId) {
            attendeeRows.push({ event_id: eventId, user_id: uid, status: "pending" });
          }
        }
      }
      if (attendeeRows.length > 0) {
        const { error: attErr } = await supabase
          .from("breakfast_attendees")
          .upsert(attendeeRows, {
            onConflict: "event_id,user_id",
            ignoreDuplicates: true,
          });
        if (attErr) throw new Error(attErr.message);
      }

      // 3. Bulk-insert potluck rows if the host provided any.
      //    `potluck_items` is now its own table (1-to-Many with
      //    breakfast_events).
      if (params.type === "potluck" && params.potluckItems?.length) {
        const rows = params.potluckItems
          .map((it) => ({ event_id: eventId, item_name: it.item_name.trim() }))
          .filter((r) => r.item_name.length > 0);

        if (rows.length > 0) {
          const { error: itemsErr } = await supabase
            .from("potluck_items")
            .insert(rows);
          if (itemsErr) throw new Error(itemsErr.message);
        }
      }

      // 4. Auto-mark host as Planned Day (best-effort, non-fatal).
      {
        const { data: trackData } = await supabase
          .from("tracked_days")
          .select("id")
          .eq("user_id", hostId)
          .eq("date", params.date)
          .maybeSingle();

        if (!trackData) {
          await supabase.from("tracked_days").upsert(
            {
              user_id:            hostId,
              date:               params.date,
              system_day_type_id: "office",
              status:             "unconfirmed",
            },
            { onConflict: "user_id,date" },
          );
        }
      }

      // 5. Fire transactional invite email (Sprint 2 — Bug 3).
      //
      // Everything up to this point is the hard contract — the event
      // and its attendees are persisted. Sending the email is a
      // separate, observable-but-non-blocking concern: we await the
      // invocation so we can surface a concrete error message to the
      // host if Resend / the Edge Function fails — but we never throw
      // from here. The event creation itself succeeded regardless.
      const newInviteeIds = (params.inviteeIds ?? []).filter((uid) => uid !== hostId);
      let emailWarning: string | null = null;

      if (newInviteeIds.length > 0) {
        try {
          const { data: emailData, error: emailErr } =
            await supabase.functions.invoke("send-event-invite", {
              body: {
                event_id:    eventId,
                invitee_ids: newInviteeIds,
                reschedule:  false,
              },
            });

          if (emailErr) {
            console.error("[useCreateEvent] send-event-invite failed:", emailErr);
            emailWarning = describeEventInviteError(emailErr);
          } else {
            // The Edge Function returns a 200 even when nothing was
            // actually sent (e.g. `RESEND_API_KEY` missing, profiles
            // opted out). Inspect the response envelope so we can
            // differentiate "dispatched" from "silently dropped" and
            // warn the host accordingly.
            const payload = (emailData ?? {}) as {
              success?: boolean;
              sent?:    number;
              skipped?: string;
              failed?:  Array<{ recipient_id: string; reason?: string }>;
              error?:   string;
            };

            if (payload.skipped === "no_resend_key") {
              emailWarning = "Event saved, but invitations were not emailed — Resend is not configured on the server yet.";
            } else if (payload.skipped === "event_not_found") {
              emailWarning = "Event saved, but the email service could not locate it. Invitations were not sent.";
            } else if (payload.error) {
              emailWarning = describeEventInvitePayloadError(payload.error);
            } else if (Array.isArray(payload.failed) && payload.failed.length > 0) {
              const reasons = payload.failed
                .map((f) => f.reason || "unknown")
                .join(", ");
              emailWarning =
                payload.sent && payload.sent > 0
                  ? `Event saved — ${payload.sent} invitation(s) sent, ${payload.failed.length} failed (${reasons}).`
                  : `Event saved, but no invitations were sent (${reasons}).`;
            }
            console.log("[useCreateEvent] send-event-invite result:", payload);
          }
        } catch (err) {
          console.error("[useCreateEvent] send-event-invite threw:", err);
          emailWarning = describeEventInviteError(err);
        }
      }

      return { eventId, emailWarning };
    },
    onSuccess: (result) => {
      // Surface email-dispatch problems so the host knows when
      // invitations didn't actually go out. We keep the variant
      // non-destructive because the event itself did save — the host
      // doesn't need a red error card, just a heads-up.
      if (result?.emailWarning) {
        toast({
          title:       "Invitations not sent",
          description: result.emailWarning,
        });
      }

      // Invalidate to ensure consistency, but RealtimeSync will catch it too.
      queryClient.invalidateQueries({ queryKey: breakfastEventsPrefix });
      queryClient.invalidateQueries({ queryKey: pendingInvitesPrefix });
      // cross-feature: circles calendar
      queryClient.invalidateQueries({ queryKey: circlesSchedulePrefix });
      // cross-feature: dashboard tracked-days grid
      queryClient.invalidateQueries({ queryKey: trackedDaysPrefix });
    },
    onError: (error: Error) => {
      toast({
        title: "Error creating event",
        description: error.message || "Something went wrong. Please try again.",
        variant: "destructive",
      });
    },
  });
}
